/**
 * UserContext - Manages user profile data and study statistics
 * 
 * =============================================================================
 * INVARIANTS (see docs/invariants/user-context-invariants.md)
 * =============================================================================
 * 
 * 4.1 SINGLE PROFILE SOURCE
 *     - No component fetches profile data independently
 *     - All profile access flows through useUser()
 *     - Derived data is built once, centrally
 * 
 * 4.2 LOADING MEANS "NOT SAFE YET"
 *     - loading === true means do not trust user data
 *     - Partial user objects are forbidden
 *     - Default placeholders must never leak into UI
 * 
 * 4.3 UPDATES ARE EXPLICIT
 *     - updateUser() mutates local state intentionally
 *     - Server writes happen separately
 *     - Failures must reconcile state, not drift silently
 * 
 * 4.4 TIMEOUTS ARE RECOVERY, NOT FAILURE
 *     - Profile load timeout triggers recovery UI
 *     - It does not invent fallback data
 *     - User must choose: retry or exit
 * 
 * =============================================================================
 * DEBUG GUIDE: Look for these prefixes in console:
 * - [UserContext] - General profile flow
 * - [UserContext:Load] - Profile loading stages
 * - [UserContext:Timeout] - Timeout mechanism
 * - [UserContext:Data] - Data fetching results
 * - [UserContext:State] - State changes
 * =============================================================================
 * 
 * FLOW:
 * 1. AuthContext detects user login → sets authUser
 * 2. UserContext sees authUser change → triggers loadUserProfile
 * 3. loadUserProfile fetches profile from Supabase
 * 4. On success: setUser(userData), setLoading(false)
 * 5. On timeout (10s): setProfileTimedOut(true), setLoading(false)
 * 6. On error: shows toast, signs out after 3s
 */
import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '../AuthContext';
import { getCurrentDateInTimezone, getRelativeDateInTimezone } from '@/utils/timezone';
import { getAvatarUrl } from '@/utils/avatars';
import { getPrestigeTitle } from "@/utils/prestigeSystem";
import { toast } from '@/hooks/use-toast';
import { CONSTANTS } from './constants';
import { User, BreakData, UserContextType, defaultUser } from './types';
import { debug } from '@/utils/debug';
import { themeDebug } from '@/utils/themeDebug';
import { trackEvent } from '@/lib/analytics';
import { useSubscriptionCheck } from '@/hooks/useSubscriptionCheck';
import { useBadgeUnlocks } from '@/hooks/useBadgeUnlocks';
import { debounce } from '@/utils/debounce';
import { useTheme } from 'next-themes';
import {
  calculateCurrentStreak,
  calculateLongestStreak,
  calculateTotalMinutes,
  calculateTotalSessions,
  calculateWeeklyMinutes
} from '@/utils/profileBuilder';

const UserContext = createContext<UserContextType | undefined>(undefined);

// V2: 10-second timeout for profile loading (2025 best practice)
const PROFILE_LOAD_TIMEOUT_MS = 10000;

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const { user: authUser } = useAuth();
  const { setTheme } = useTheme();
  const [user, setUser] = useState<User>(defaultUser);
  const [loading, setLoading] = useState(false);
  const [profileTimedOut, setProfileTimedOut] = useState(false); // V2: Timeout state
  const isLoadingProfile = useRef(false);
  const authUserRef = useRef(authUser);
  const loadTimeoutRef = useRef<NodeJS.Timeout | null>(null); // V2: Timeout ref
  const hasAppliedThemeRef = useRef<string | null>(null); // Theme tracking ref - declared early for logout access
  
  // ==========================================================================
  // REF SYNC: Keep authUser ref updated for use in debounced callbacks
  // ==========================================================================
  useEffect(() => {
    debug.log('[UserContext:State] authUserRef updated:', authUser?.id);
    authUserRef.current = authUser;
  }, [authUser]);
  
  // ==========================================================================
  // DEBOUNCED LOADER: Prevents multiple rapid profile loads
  // Debounce delay: 300ms
  // ==========================================================================
  const loadUserProfileDebounced = useRef(
    debounce(() => {
      debug.log('[UserContext:Load] 🔄 Debounce fired, checking conditions...');
      debug.log('[UserContext:Load] isLoadingProfile.current:', isLoadingProfile.current);
      debug.log('[UserContext:Load] authUserRef.current:', authUserRef.current?.id);
      
      if (!isLoadingProfile.current && authUserRef.current) {
        debug.log('[UserContext:Load] ✅ Conditions met, calling loadUserProfile');
        loadUserProfile();
      } else {
        debug.log('[UserContext:Load] ⚠️ Conditions NOT met, skipping');
      }
    }, 300)
  ).current;

  // ==========================================================================
  // RETRY FUNCTION: Manual retry for recovery UI
  // ==========================================================================
  const retryProfileLoad = useCallback(() => {
    debug.log('[UserContext] 🔄 Manual retry triggered');
    setProfileTimedOut(false);
    isLoadingProfile.current = false; // Reset so load can proceed
    if (authUserRef.current) {
      loadUserProfile();
    }
  }, []);

  // ==========================================================================
  // MAIN EFFECT: Triggered when authUser changes (from AuthContext)
  // This is the primary entry point for profile loading
  // ==========================================================================
  useEffect(() => {
    debug.log('[UserContext] 👀 Auth user changed effect triggered');
    debug.log('[UserContext] authUser:', authUser ? { id: authUser.id, email: authUser.email } : null);
    
    if (authUser && authUser.id) {
      debug.log('[UserContext:Load] 🚀 Starting profile load sequence for:', authUser.id);
      
      // Set loading immediately BEFORE debounce to prevent rendering with defaultUser
      debug.log('[UserContext:State] Setting loading=true, profileTimedOut=false');
      setLoading(true);
      setProfileTimedOut(false); // V2: Reset timeout state
      
      // V2: Clear any existing timeout
      if (loadTimeoutRef.current) {
        debug.log('[UserContext:Timeout] Clearing existing timeout');
        clearTimeout(loadTimeoutRef.current);
      }
      
      // ======================================================================
      // TIMEOUT MECHANISM: 10-second safety net for stuck profile loads
      // If profile doesn't load in 10s, show recovery UI
      // ======================================================================
      debug.log('[UserContext:Timeout] ⏱️ Setting 10-second timeout');
      loadTimeoutRef.current = setTimeout(() => {
        debug.log('[UserContext:Timeout] ⏰ Timeout fired!');
        debug.log('[UserContext:Timeout] isLoadingProfile.current:', isLoadingProfile.current);
        debug.log('[UserContext:Timeout] Current loading state:', loading);
        
        if (isLoadingProfile.current) {
          debug.log('[UserContext:Timeout] ❌ Profile load timed out after 10 seconds');
          debug.warn('[UserContext] Profile load timed out after 10 seconds');
          setProfileTimedOut(true);
          setLoading(false); // V3: Allow ProtectedRoute to proceed past loading check
          isLoadingProfile.current = false; // V3: Reset ref so retry can work
        } else {
          debug.log('[UserContext:Timeout] ℹ️ Timeout fired but isLoadingProfile is already false');
        }
      }, PROFILE_LOAD_TIMEOUT_MS);
      
      // Trigger debounced profile load
      debug.log('[UserContext:Load] Calling loadUserProfileDebounced...');
      loadUserProfileDebounced();
    } else {
      // ======================================================================
      // LOGOUT PATH: Reset all state when user signs out
      // ======================================================================
      debug.log('[UserContext] 🚪 No authUser - resetting to default state');
      
      // Reset theme tracking ref to allow fresh theme application on next login
      hasAppliedThemeRef.current = null;
      
      // V3: NO setTheme on logout - theme is a DEVICE PREFERENCE, not identity
      // The next user who logs in will get their profile theme via initialize-only logic
      setUser(defaultUser);
      setLoading(false);
      setProfileTimedOut(false); // V2: Reset timeout on logout
      
      // V2: Clear timeout on logout
      if (loadTimeoutRef.current) {
        debug.log('[UserContext:Timeout] Clearing timeout (logout)');
        clearTimeout(loadTimeoutRef.current);
        loadTimeoutRef.current = null;
      }
    }
    
    // V2: Cleanup timeout on unmount
    return () => {
      if (loadTimeoutRef.current) {
        debug.log('[UserContext:Timeout] Cleanup - clearing timeout');
        clearTimeout(loadTimeoutRef.current);
        loadTimeoutRef.current = null;
      }
    };
  // INVARIANT: Depend on authUser?.id, not authUser object (see docs/invariants/PHASE_2_USEEFFECT.md §1.5)
  // Token refresh creates new object references with same ID - should NOT trigger profile reload
  }, [authUser?.id]);

  // ==========================================================================
  // EVENT LISTENER: Manual profile refresh from other components
  // Debounced to prevent cascade loops from rapid event dispatches
  // ==========================================================================
  const lastProfileEventRef = useRef<number>(0);
  
  useEffect(() => {
    const handleProfileUpdate = () => {
      // Throttle: Ignore events within 2 seconds of each other
      const now = Date.now();
      if (now - lastProfileEventRef.current < 2000) {
        debug.log('[UserContext] 📡 Profile update event THROTTLED - too soon after last event');
        return;
      }
      lastProfileEventRef.current = now;
      
      // Guard against concurrent loads to prevent cascade freeze
      if (isLoadingProfile.current) {
        debug.log('[UserContext] 📡 Profile update event IGNORED - load already in progress');
        return;
      }
      debug.log('[UserContext] 📡 Manual profile refresh triggered via event');
      loadUserProfile(0, true); // Silent reload to prevent loading flash
    };

    window.addEventListener('profile-updated', handleProfileUpdate);
    return () => window.removeEventListener('profile-updated', handleProfileUpdate);
  }, [authUser]);

  // ==========================================================================
  // LOAD USER PROFILE: Main profile fetching function
  // Called by: debounced loader, retry button, profile-updated event
  // ==========================================================================
  const loadUserProfile = async (retryCount = 0, silent = false) => {
    // ======================================================================
    // STALE CLOSURE FIX: Capture ref value at function start
    // The debounced function calls this 300ms later, but authUser from
    // closure may be stale. authUserRef.current is always up-to-date.
    // ======================================================================
    const currentAuthUser = authUserRef.current;
    
    debug.log('[UserContext:Load] ========================================');
    debug.log('[UserContext:Load] 📥 loadUserProfile called');
    debug.log('[UserContext:Load] currentAuthUser:', currentAuthUser?.id);
    debug.log('[UserContext:Load] retryCount:', retryCount);
    debug.log('[UserContext:Load] isLoadingProfile.current:', isLoadingProfile.current);
    
    // Debug: Detect stale closure (should never trigger after fix)
    if (authUser !== currentAuthUser) {
      debug.warn('[UserContext:Load] ⚠️ STALE CLOSURE DETECTED!');
      debug.warn('[UserContext:Load] authUser (closure):', authUser?.id);
      debug.warn('[UserContext:Load] authUserRef.current:', currentAuthUser?.id);
    }
    debug.log('[UserContext:Load] ========================================');
    
    // ----------------------------------------------------------------------
    // GUARD: No auth user - abort
    // ----------------------------------------------------------------------
    if (!currentAuthUser) {
      debug.log('[UserContext:Load] ❌ No authUser, aborting');
      setLoading(false);
      isLoadingProfile.current = false;
      return;
    }
    
    // ----------------------------------------------------------------------
    // GUARD: Prevent concurrent profile loads
    // ----------------------------------------------------------------------
    if (isLoadingProfile.current) {
      debug.log('[UserContext:Load] ⚠️ Profile load already in progress, skipping');
      return;
    }

    // ----------------------------------------------------------------------
    // START LOADING: Set flags and fetch profile
    // ----------------------------------------------------------------------
    debug.log('[UserContext:Load] ✅ Starting actual profile fetch (silent:', silent, ')');
    debug.log('[UserContext:State] Setting isLoadingProfile.current = true');
    isLoadingProfile.current = true;
    if (!silent) {
      setLoading(true);
    }
    
    try {
      debug.log('[UserContext:Data] 🔍 Fetching profile from Supabase...');
      const { data: profile, error } = await supabase
        .from('user_profiles')
        .select('*, use_initials_avatar, study_profile, streak_freeze_days_remaining, streak_freeze_days_reset_date, streak_freeze_days_used_this_period, theme, first_study_done, tutorial_progress')
        .eq('id', currentAuthUser.id)
        .single();

      debug.log('[UserContext:Data] Profile fetch result:');
      debug.log('[UserContext:Data] - Has profile:', !!profile);
      debug.log('[UserContext:Data] - Error:', error?.message || 'none');
      if (profile) {
        debug.log('[UserContext:Data] - Handle:', profile.handle);
        debug.log('[UserContext:Data] - Onboarding:', profile.onboarding_completed);
        debug.log('[UserContext:Data] - Premium:', profile.premium);
      }

      // --------------------------------------------------------------------
      // ERROR HANDLING: Auth errors during profile fetch
      // --------------------------------------------------------------------
      if (error) {
        debug.log('[UserContext:Data] ❌ Error occurred:', error.code, error.message);
        
        // Check if it's an auth error (401/403 or JWT error)
        if (error.code === 'PGRST301' || error.message?.includes('JWT')) {
          debug.log('[UserContext:Data] 🔐 Auth error detected - will sign out');
          // Dispatch global auth error event
          window.dispatchEvent(new CustomEvent('auth-error', { 
            detail: { status: 401, message: error.message, context: 'profile_fetch' } 
          }));

          toast({
            title: "Session expired",
            description: "Your session expired — please sign in again.",
            variant: "destructive"
          });

          // V2: Use supabase.auth.signOut() instead of window.location.href
          // Per theme-architecture.md: Full reloads cause theme bootstrap re-run → flicker
          setTimeout(async () => {
            debug.log('[UserContext] Auto-signing out after auth error');
            await supabase.auth.signOut();
            // Guards will handle redirect to /auth
          }, 3000);
          return;
        }

        // Profile not found - might be incomplete account (PGRST116)
        if (error.code === 'PGRST116') {
          debug.log('[UserContext:Data] ⚠️ Profile not found (PGRST116) - incomplete account');
          // Handle incomplete profile
          setUser((prev) => ({ ...prev, error: 'profile_incomplete' } as any));
          setLoading(false);
          isLoadingProfile.current = false;
          return;
        }

        throw error;
      }

      // --------------------------------------------------------------------
      // HANDLE VALIDATION: Retry if handle is missing or temporary
      // --------------------------------------------------------------------
      if ((!profile.handle || profile.handle.startsWith('temp_')) && retryCount < 2) {
        debug.log('[UserContext:Load] ⏳ Handle missing/temporary, retrying...', retryCount + 1);
        debug.warn(`Profile handle is ${profile.handle || 'NULL'}, retry ${retryCount + 1}/2`);
        await new Promise(resolve => setTimeout(resolve, 800));
        return loadUserProfile(retryCount + 1);
      }

      // Critical: If handle is still missing after retries, this is a data integrity issue
      if (!profile.handle) {
        debug.error('[UserContext:Data] ❌ CRITICAL: Handle is NULL after retries');
        debug.error('CRITICAL: User profile loaded but handle is NULL. This should never happen after database migration.');
        setUser({
          ...defaultUser,
          id: currentAuthUser.id,
          username: 'Unknown User',
          handle: 'unknown_user',
          error: 'profile_incomplete'
        } as any);
        setLoading(false);
        return;
      }

      debug.log('[UserContext:Load] ✅ Profile validated, building user data...');

      const userTimezone = profile.timezone || "UTC";
      const today = getCurrentDateInTimezone(userTimezone);
      
      const { data: todayData } = await supabase
        .from('daily_totals')
        .select('minutes, sessions')
        .eq('user_id', currentAuthUser.id)
        .eq('day', today)
        .maybeSingle();

      const { data: totalData } = await supabase
        .from('daily_totals')
        .select('day, minutes, sessions')
        .eq('user_id', currentAuthUser.id);

      // PHASE B: Use extracted pure functions from profileBuilder.ts
      const totalMinutes = calculateTotalMinutes(totalData);
      const totalSessions = calculateTotalSessions(totalData);
      const totalHours = Math.floor(totalMinutes / 60);

      // Fetch freeze log from database
      const { data: freezeLog } = await supabase
        .from('streak_freeze_log')
        .select('frozen_date')
        .eq('user_id', currentAuthUser.id);

      // PHASE B: Use extracted streak calculation
      const currentStreak = calculateCurrentStreak(
        totalData,
        userTimezone,
        freezeLog,
        getCurrentDateInTimezone,
        getRelativeDateInTimezone
      );

      // Track streak_extended event if streak increased
      const previousStreak = parseInt(localStorage.getItem('lastKnownStreak') || '0');
      
      if (currentStreak > previousStreak && currentStreak > 0) {
        trackEvent('streak_extended', {
          new_streak: currentStreak,
          previous_streak: previousStreak,
          streak_increase: currentStreak - previousStreak,
          user_timezone: profile.timezone || 'UTC',
          has_freeze_protection: (profile.streak_freeze_days_remaining || 0) > 0,
          freeze_days_remaining: profile.streak_freeze_days_remaining || 0,
        });

        localStorage.setItem('lastKnownStreak', currentStreak.toString());
      }

      // Check if user is admin (server-side verification)
      const { data: isAdminCheck } = await supabase.rpc('has_role', {
        _user_id: currentAuthUser.id,
        _role: 'admin'
      });
      const isAdmin = isAdminCheck || false;

      // PHASE B: Use extracted longest streak calculation
      const longestStreak = calculateLongestStreak(totalData, currentStreak);

      const calculatedPrestigeTitle = getPrestigeTitle(totalHours);

      const sevenDaysAgo = getRelativeDateInTimezone(-7, profile.timezone || 'UTC');
      const { data: weeklyData } = await supabase
        .from('daily_totals')
        .select('minutes, sessions')
        .eq('user_id', profile.id)
        .gte('day', sevenDaysAgo)
        .order('day', { ascending: false });

      // PHASE B: Use extracted weekly calculation
      const weeklyMinutes = calculateWeeklyMinutes(weeklyData);

      const userData: User = {
        id: profile.id,
        handle: profile.handle,
        username: profile.handle,
        email: currentAuthUser.email || '',
        avatar: getAvatarUrl(
          profile.avatar_seed || 'default-user',
          profile.use_initials_avatar || false,
          profile.handle
        ),
        avatar_seed: profile.avatar_seed,
        isPremium: profile.premium || false,
        customTimerUnlocked: profile.custom_timer_unlocked || false,
        prestigeTitle: calculatedPrestigeTitle,
        level: Math.floor(totalMinutes / 60) + 1,
        coins: profile.coins || 100,
        // Fetch user leaderboard position (rank, activity status, percentile)
        ...await (async () => {
          try {
            const { data: positionData, error } = await supabase
              .rpc('get_user_leaderboard_position', {
                p_user_id: profile.id,
                days_back: 6, // Weekly leaderboard
                metric_type: 'time',
                user_timezone: profile.timezone || 'UTC'
              });
            
            if (error) {
              debug.error('Error fetching user rank:', error);
              return { rank: null, hasActivity: false, percentile: null };
            }

            const userPosition = positionData?.[0];
            
            return {
              rank: userPosition?.has_activity ? userPosition.rank : null,
              hasActivity: userPosition?.has_activity || false,
              percentile: userPosition?.percentile || null
            };
          } catch (error) {
            debug.error('Error fetching user rank:', error);
            return { rank: null, hasActivity: false, percentile: null };
          }
        })(),
        totalStudyHours: Math.floor(totalMinutes / 60),
        totalSessions: totalSessions,
        studyDays: totalData?.length || 0,
        currentStreak,
        longestStreak,
        streak: currentStreak,
        current_streak: currentStreak,
        timeSpent: todayData?.minutes || 0,
        longest_streak: longestStreak,
        study_days: totalData?.length || 0,
        sessions_today: todayData?.sessions || 0,
        weekly_time: weeklyMinutes,
        total_sessions: totalSessions,
        todayStudyTime: todayData?.minutes || 0,
        todaySessionCount: todayData?.sessions || 0,
        weeklyStudyTime: weeklyMinutes,
        lastStudyDate: today,
        lastSessionEndTime: '',
        achievements: [],
        stars: Math.floor(totalMinutes / 30),
        avatarBackground: "#6366f1",
        avatarFrame: "basic", 
        avatarColor: "#ffffff",
        avatarConfig: {},
        daily_goal_minutes: profile.daily_goal_minutes || 120,
        daily_goal_last_changed: profile.daily_goal_last_changed,
        daily_goal_changes_count: profile.daily_goal_changes_count || 0,
        dailyGoal: profile.daily_goal_minutes || 120,
        goalHours: 1000,
        quote: profile.quote || "",
        country: profile.country || "",
        state: profile.state || "",
        city: profile.city || "",
        timezone: profile.timezone || "UTC",
        following: [],
        selected_profile_stats: profile.selected_profile_stats || ['rank', 'total_time', 'current_streak', 'stars'],
        show_city_on_profile: profile.show_city_on_profile !== undefined ? profile.show_city_on_profile : true,
        use_initials_avatar: profile.use_initials_avatar || false,
        study_profile: (profile.study_profile as 'solo' | 'social') || 'solo',
        prestigeCount: 0,
        email_notifications: profile.email_notifications ?? true,
        preferred_reminder_time: profile.preferred_reminder_time || '09:00:00',
        break_compliance_rate: profile.break_compliance_rate,
        avg_break_duration: profile.avg_break_duration,
        breaks_skipped_count: profile.breaks_skipped_count,
        forge_heat: profile.forge_heat || 0,
        custom_forge_cycle: profile.custom_forge_cycle as { study: number; break: number; preset?: string } | null,
        custom_forge_cycles: profile.custom_forge_cycles as Array<{ name: string; study: number; break: number }> | null,
        streak_freeze_days_remaining: profile.streak_freeze_days_remaining || 0,
        streak_freeze_days_reset_date: profile.streak_freeze_days_reset_date,
        streak_freeze_days_used_this_period: profile.streak_freeze_days_used_this_period || 0,
        sound_effects_enabled: profile.sound_effects_enabled ?? true,
        theme: profile.theme || 'default',
        isAdmin: isAdmin,
        first_study_done: profile.first_study_done ?? false,
        tutorial_progress: (profile.tutorial_progress as Record<string, boolean>) || {}
      };

      // Store theme in userData - actual theme application happens in separate useEffect
      // This prevents theme thrashing during profile load cycles
      userData.theme = profile.theme || 'charcoal-pro';

      debug.log('[UserContext] Loaded freeze data:', {
        streak_freeze_days_remaining: profile.streak_freeze_days_remaining,
        streak_freeze_days_reset_date: profile.streak_freeze_days_reset_date,
        streak_freeze_days_used_this_period: profile.streak_freeze_days_used_this_period,
        premium: profile.premium
      });

      // ====================================================================
      // SUCCESS: Set user data
      // ====================================================================
      debug.log('[UserContext:State] ✅ Setting user data');
      debug.log('[UserContext:State] - id:', userData.id);
      debug.log('[UserContext:State] - handle:', userData.handle);
      debug.log('[UserContext:State] - isPremium:', userData.isPremium);
      debug.log('[UserContext:State] - rank:', userData.rank);
      setUser(userData);
      
    } catch (error: any) {
      // ====================================================================
      // ERROR: Unexpected error during profile load
      // ====================================================================
      debug.error('[UserContext:Load] ❌ Unexpected error:', error);
      debug.error('Error loading user profile:', error);
      
      // Dispatch global auth error event for unexpected errors
      window.dispatchEvent(new CustomEvent('auth-error', { 
        detail: { status: 500, message: error.message, context: 'profile_load_unexpected' } 
      }));
      
      toast({
        title: "Profile load failed",
        description: "Unable to load your profile. Signing out...",
        variant: "destructive"
      });

      // V2: Use supabase.auth.signOut() instead of window.location.href
      // Per theme-architecture.md: Full reloads cause theme bootstrap re-run → flicker
      setTimeout(async () => {
        debug.log('[UserContext] Auto-signing out after unexpected error');
        await supabase.auth.signOut();
        // Guards will handle redirect to /auth
      }, 3000);
      
    } finally {
      // ====================================================================
      // FINALLY: Always reset loading state
      // ====================================================================
      debug.log('[UserContext:Load] 🏁 Finally block reached');
      debug.log('[UserContext:State] Setting loading=false, isLoadingProfile.current=false');
      setLoading(false);
      isLoadingProfile.current = false;
      
      // V2: Clear timeout on successful load or error
      if (loadTimeoutRef.current) {
        debug.log('[UserContext:Timeout] Clearing timeout (load complete)');
        clearTimeout(loadTimeoutRef.current);
        loadTimeoutRef.current = null;
      }
    }
  };

  // Use badge unlocks hook
  const { checkForNewBadges } = useBadgeUnlocks(authUser?.id || '');
  
  // Check subscription status (event-driven, no polling)
  // The hook now dispatches 'profile-updated' event directly, so callback is a no-op
  useSubscriptionCheck(authUser, user.isPremium, () => {
    debug.log('[UserContext] Subscription callback triggered (no-op, event handles reload)');
  });

  // ==========================================================================
  // THEME APPLICATION: Separate from profile load to prevent thrashing
  // Only applies theme AFTER user state is fully set
  // Declared early so it can be reset in the logout path above
  // ==========================================================================
  // NOTE: hasAppliedThemeRef is moved before the main useEffect to allow access in logout path
  
  useEffect(() => {
    // ==========================================================================
    // THEME INITIALIZATION ONLY - NOT FOR USER-INITIATED CHANGES
    // Per theme-architecture.md: This effect only runs on INITIAL PROFILE LOAD
    // ThemeSwitcher handles user-initiated theme changes directly via next-themes
    // ==========================================================================
    
    // Only apply theme once user is loaded and has a valid ID
    if (!user.id || loading) return;
    
    // If theme has already been applied this session, skip entirely
    // This prevents this effect from interfering with ThemeSwitcher
    if (hasAppliedThemeRef.current !== null) {
      debug.log('[UserContext] Theme already applied this session, skipping');
      return;
    }
    
    // V2: LOCAL-FIRST theme sync - respect existing device preference
    // Per theme-architecture.md: Theme is device preference, not identity
    const localTheme = localStorage.getItem('studyforge-theme');
    if (localTheme) {
      // User already has a theme preference on this device - respect it
      themeDebug.skipped('UserContext', `Local theme exists: ${localTheme}`);
      hasAppliedThemeRef.current = localTheme;
      return; // Don't override local choice with DB theme
    }
    
    // No local theme - apply from profile (first login on this device)
    const targetTheme = user.theme || 'charcoal-pro';
    
    // Check if theme requires premium
    const PREMIUM_THEMES = ['cafe-mocha', 'royal-scholar', 'golden-scholar'];
    const isPremiumTheme = PREMIUM_THEMES.includes(targetTheme);
    
    let themeToApply = targetTheme;
    
    if (isPremiumTheme && !user.isPremium) {
      // User has premium theme but no premium access - downgrade to default
      themeDebug.downgrade(targetTheme, 'charcoal-pro');
      themeToApply = 'charcoal-pro';
      
      // Update database to persist the downgrade (fire-and-forget)
      if (authUser?.id) {
        supabase
          .from('user_profiles')
          .update({ theme: 'charcoal-pro' })
          .eq('id', authUser.id)
          .then(({ error }) => {
            if (error) debug.error('Failed to update theme after downgrade:', error);
          });
          
        trackEvent('theme_auto_downgraded', {
          from_theme: targetTheme,
          to_theme: 'charcoal-pro',
          reason: 'premium_expired',
          triggered_at: 'auth'
        });
      }
    }
    
    themeDebug.applied('UserContext', themeToApply, 'First login on device - no local theme');
    hasAppliedThemeRef.current = themeToApply;
    setTheme(themeToApply);
    // NOTE: setTheme intentionally removed from deps - this is initialization only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  // V2: Tightened deps per theme-architecture.md - theme sync is initialization, not reactive
  // user.theme and user.isPremium removed - we check them inside but don't re-run when they change
  }, [user.id, loading]);

  const updateUser = useCallback(async (updates: Partial<User>) => {
    // Sync dailyGoal with daily_goal_minutes if daily_goal_minutes is being updated
    const syncedUpdates = { ...updates };
    if (updates.daily_goal_minutes !== undefined) {
      syncedUpdates.dailyGoal = updates.daily_goal_minutes;
    }
    
    // Recompute derived avatar if inputs changed
    if (updates.avatar_seed !== undefined || updates.use_initials_avatar !== undefined) {
      const newSeed = updates.avatar_seed ?? user.avatar_seed ?? 'default-user';
      const useInitials = updates.use_initials_avatar ?? user.use_initials_avatar ?? false;
      const handle = updates.handle ?? user.handle;
      syncedUpdates.avatar = getAvatarUrl(newSeed, useInitials, handle);
    }
    
    // Debug: Track coin updates specifically
    if (updates.coins !== undefined) {
      debug.log('[UserContext] Coin update received:', { 
        previousCoins: user.coins, 
        newCoins: updates.coins 
      });
    }
    
    setUser(prev => ({ ...prev, ...syncedUpdates }));
    
    if (authUser && updates.isPremium !== undefined) {
      try {
        await supabase
          .from('user_profiles')
          .update({ premium: updates.isPremium })
          .eq('id', authUser.id);
      } catch (error) {
        debug.error('Error updating premium status:', error);
      }
    }

    if (authUser && updates.quote !== undefined) {
      try {
        await supabase
          .from('user_profiles')
          .update({ quote: updates.quote })
          .eq('id', authUser.id);
      } catch (error) {
        debug.error('Error updating quote:', error);
      }
    }
  }, [authUser, user.avatar_seed, user.use_initials_avatar, user.handle, user.coins]);

  // NOTE: awardCoins has been REMOVED - all coin operations now use atomic RPCs:
  // - Session coins: validate-study-session → increment_user_coins RPC
  // - Break coins: updateBreakStats → modify_user_coins RPC
  // - Shop purchases: Shop.tsx → modify_user_coins RPC
  // This eliminates read-then-write race conditions. See docs/timer-architecture.md.

  const updateStreak = () => {
    // Streak calculation handled by database
  };

  const canStartSession = () => {
    const now = new Date();
    
    if (user.lastSessionEndTime) {
      const lastEnd = new Date(user.lastSessionEndTime);
      const timeSinceEnd = (now.getTime() - lastEnd.getTime()) / (1000 * 60);
      
      if (timeSinceEnd < CONSTANTS.BREAK_REQUIRED_MINUTES) {
        const waitTime = Math.ceil(CONSTANTS.BREAK_REQUIRED_MINUTES - timeSinceEnd);
        return { 
          canStart: false, 
          reason: `Break required: ${waitTime} minute${waitTime !== 1 ? 's' : ''} remaining` 
        };
      }
    }
    
    return { canStart: true };
  };

  // SIMPLIFIED: addStudySession now ONLY reloads profile and checks badges
  // Database writes are handled by:
  // - Edge function (production): INSERT study_session + upsert_daily_totals + update coins
  // - Timer.tsx dev mode: INSERT study_session + upsert_daily_totals
  // This prevents DUPLICATE database writes that were causing 2-3x star accumulation
  const addStudySession = async (minutes: number, silent = false) => {
    if (!authUser) return;
    
    debug.log('[UserContext] addStudySession called - reloading profile (silent:', silent, ')');
    
    try {
      // Silent reload prevents ProtectedRoute from showing skeleton during session completion
      await loadUserProfile(0, silent);
      
      // Check for badge unlocks after session completion
      if (authUser?.id) {
        await checkForNewBadges(user);
      }
      
    } catch (error) {
      debug.error('Error in addStudySession:', error);
    }
  };

  const resetWeeklyStats = () => {
    setUser(prev => ({
      ...prev,
      weeklyStudyTime: 0
    }));
  };

  const updateBreakStats = async (breakData: BreakData) => {
    if (!authUser?.id) return;

    try {
      // 1. Update session record (non-coin data)
      await supabase
        .from('study_sessions')
        .update({
          recommended_break_minutes: breakData.recommendedMinutes,
          actual_break_minutes: breakData.actualMinutes,
          break_completed: breakData.completed,
          break_activities: breakData.activities,
        })
        .eq('id', breakData.sessionId);

      // 2. Atomic coin award via RPC (eliminates race condition)
      let newCoinBalance = user.coins;
      if (breakData.coinsEarned > 0) {
        const { data: coinResult, error: coinError } = await supabase.rpc('modify_user_coins', {
          p_user_id: authUser.id,
          p_amount: breakData.coinsEarned,
          p_reason: 'break_reward',
          p_reference_id: breakData.sessionId
        });
        
        if (coinError) {
          debug.error('Error awarding break coins:', coinError);
        } else if (coinResult && typeof coinResult === 'object' && 'success' in coinResult && coinResult.success) {
          const result = coinResult as { success: boolean; new_balance: number };
          if (typeof result.new_balance === 'number') {
            newCoinBalance = result.new_balance;
          }
        }
      }

      // 3. Calculate compliance stats
      const { data: complianceData } = await supabase
        .rpc('calculate_break_compliance', { p_user_id: authUser.id });

      const { data: sessions } = await supabase
        .from('study_sessions')
        .select('actual_break_minutes')
        .eq('user_id', authUser.id)
        .not('actual_break_minutes', 'is', null);

      const avgBreak = sessions?.length
        ? sessions.reduce((sum, s) => sum + (s.actual_break_minutes || 0), 0) / sessions.length
        : 5;

      // 4. Update profile stats (coins already updated atomically via RPC)
      await supabase
        .from('user_profiles')
        .update({
          break_compliance_rate: complianceData || 100,
          avg_break_duration: Math.round(avgBreak),
          breaks_skipped_count: breakData.completed
            ? user.breaks_skipped_count
            : (user.breaks_skipped_count || 0) + 1,
        })
        .eq('id', authUser.id);

      // 5. Sync local state with server-authoritative coin balance
      setUser(prev => ({
        ...prev,
        coins: newCoinBalance,
        break_compliance_rate: complianceData || 100,
        avg_break_duration: Math.round(avgBreak),
        breaks_skipped_count: breakData.completed
          ? prev.breaks_skipped_count
          : (prev.breaks_skipped_count || 0) + 1,
      }));
      
      // Check for badge unlocks after break completion
      if (authUser?.id && breakData.completed) {
        await checkForNewBadges(user);
      }
    } catch (error) {
      debug.error('Error updating break stats:', error);
      toast({
        title: "Break Update Failed",
        description: "Your break wasn't recorded. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <UserContext.Provider value={{
      user,
      updateUser,
      updateStreak,
      canStartSession,
      addStudySession,
      resetWeeklyStats,
      updateBreakStats,
      loading,
      // V2: Timeout handling
      profileTimedOut,
      retryProfileLoad,
    }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
